/**
 * `PlanSavers` for `scope.kind === 'tenant'` (P4 Task 1) — pure wiring from
 * `<PlanEditor>`'s abstract saver axes onto the concrete per-tenant
 * `budgetApi` setters. `features/billing/ui` cannot import `budgetApi`
 * (that closes features/orgs → features/billing → features/orgs — see
 * `plan-editor-scope.ts`'s module docblock), so this lives on the orgs side
 * and is INJECTED into `<PlanEditor scope={{kind:'tenant', ...}}>` from
 * `CustomerDetail.tsx`'s Plan tab (P4 Task 2).
 *
 * Same seam as `CatalogAdmin.tsx`'s `planSavers(p)` (`plan` scope) and
 * `PlanEditor.tsx`'s `useDraftSavers` (`plan-create` scope) — one `PlanSavers`
 * builder per scope, each closing over its own persistence target.
 *
 * ── What tenant scope does NOT support ────────────────────────────────────
 *
 * `PlanScalarPatch.name` renames the PLAN. In `plan`/`plan-create` scope that
 * is the catalog row itself; in `tenant` scope the "plan" is a shared preset
 * that OTHER tenants are also on (see CLAUDE.md's "Preset customisation" —
 * `plan_customized_at` exists precisely because a preset keeps governing many
 * tenants after it seeds them). There is no per-tenant name override to write
 * it to, and silently dropping the field would let an operator believe they
 * renamed this customer's plan when nothing happened. So `saveScalars`
 * REFUSES a `name` patch rather than routing it anywhere or no-op'ing it.
 *
 * ── Currency ───────────────────────────────────────────────────────────────
 *
 * `billingCurrency` is accepted for symmetry with the OTHER tenant-scope
 * editors on the Plan tab (`LlmPricing`/`FeaturePricingEditor`, which take a
 * `scope`/`scopeId`/`currency` triple directly per `plan-editor-scope.ts`'s
 * docblock — "NOT covered by `PlanSavers`"). None of the FOUR `PlanSavers`
 * axes is currency-denominated: uploads is a boolean, the cycle is a
 * count+unit, rate limits are per-tier integers, and the scalar axes this
 * adapter DOES route (seats, storage, concurrency) carry no money. The
 * parameter is kept on the signature per the brief rather than dropped, so a
 * future money-bearing axis added to `PlanSavers` has it without an API
 * change at every call site.
 */
import { budgetApi } from '../../client/budget'
import { inBatch, type PinnedBatch } from '@papan/api-client'
import type { PlanSavers, PlanScalarPatch, PlanScalarSaveResult } from '@papan/billing/ui'

export function tenantPlanSavers(tenantId: string, billingCurrency: string): PlanSavers {
  // Not used by any axis today — see the module docblock's Currency section.
  void billingCurrency

  return {
    async saveScalars(patch: PlanScalarPatch, batch?: PinnedBatch): Promise<PlanScalarSaveResult> {
      if (patch.name !== undefined) {
        throw new Error(
          'saveScalars: renaming a plan is not supported in tenant scope — ' +
            'the plan is a shared preset other tenants are also on',
        )
      }

      if (patch.includedSeats !== undefined) {
        await inBatch(batch, () => budgetApi.setIncludedSeats(tenantId, patch.includedSeats!))
      }
      if (patch.maxSeats !== undefined) {
        await inBatch(batch, () => budgetApi.setSeatCap(tenantId, patch.maxSeats!))
      }

      // Storage and concurrency both write through the single tenant-caps
      // route — batch them into ONE call when a patch touches both, rather
      // than two round trips racing each other.
      const overrides: Parameters<typeof budgetApi.setTenantOverrides>[1] = {}
      if (patch.storageBytes !== undefined) {
        overrides.storage = { state: 'finite', value: patch.storageBytes }
      }
      if (patch.concurrency !== undefined) {
        overrides.concurrency =
          patch.concurrency === null ? { state: 'inherit' } : { state: 'finite', value: patch.concurrency }
      }
      if (Object.keys(overrides).length > 0) {
        await inBatch(batch, () => budgetApi.setTenantOverrides(tenantId, overrides))
      }

      // No OTHER tenants to warn about — this edits ONE tenant's own numbers,
      // never a preset shared with subscribers. See `PlanScalarSaveResult`'s
      // docblock in `plan-editor-scope.ts`.
      return {}
    },

    async saveUploads(canCreate: boolean | null, batch?: PinnedBatch): Promise<void> {
      await inBatch(batch, () => budgetApi.setTenantUploads(tenantId, canCreate))
    },

    /**
     * UNREACHABLE, and it must stay that way — the tenant scope no longer
     * renders `PlanCycle` (see `PlanEditor`). It threw nothing and wrote
     * `tenant_settings.cycle_count`/`.cycle_unit`, which 0185 retired for
     * money: `buildAmountDue` takes the rhythm from the CURRENT TERM.
     *
     * Kept only because `PlanSavers` requires the member, and made to THROW so
     * that re-rendering the control in this scope fails loudly instead of
     * silently recording a rhythm no invoice will use. The rhythm is set on
     * the Billing tab's term form.
     */
    async saveCycle(): Promise<void> {
      throw new Error(
        'The tenant billing rhythm lives on `manual_subscription_terms` since 0185 — '
        + 'set it through the Billing tab\'s subscription-term form, not here. '
        + '`tenant_settings.cycle_*` is not read by `buildAmountDue`.',
      )
    },

    async saveRateLimits(limits, batch): Promise<void> {
      await inBatch(batch, () => budgetApi.setTenantRateLimits(tenantId, limits))
    },

    // free/extra/grace, ONE `setTenantOverrides` call (Task 6, P4) — the
    // NEW `tenantCaps` bar section routes here; seats/storage/concurrency
    // stay on `saveScalars` above, they do NOT move.
    async saveCaps(patch, batch): Promise<void> {
      const overrides: Parameters<typeof budgetApi.setTenantOverrides>[1] = {}
      if (patch.free !== undefined) {
        overrides.free = patch.free === null ? { state: 'inherit' } : { state: 'finite', value: patch.free }
      }
      if (patch.extra !== undefined) {
        // Blank commits `inherit`, never `disabled` — a blank field means
        // "follow the preset", not "explicitly turn off overage".
        overrides.extra = patch.extra === null ? { state: 'inherit' } : { state: 'finite', value: patch.extra }
      }
      if (patch.grace !== undefined) {
        overrides.grace = patch.grace === null ? { state: 'inherit' } : { state: 'finite', value: patch.grace }
      }
      if (Object.keys(overrides).length > 0) {
        await inBatch(batch, () => budgetApi.setTenantOverrides(tenantId, overrides))
      }
    },

    async saveEnforcement(mode: 'enforce' | 'warn_only', batch?: PinnedBatch): Promise<void> {
      await inBatch(batch, () => budgetApi.setTenantEnforcementMode(tenantId, mode))
    },
  }
}
